import Divider from '@mui/material/Divider/Divider'
import { ConsumptionChartData } from '@vae/server/src/utils/arrays/types/ChartData'
import dayjs from 'dayjs'
import React, { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ConsumptionDataContext } from '../../../../../contexts/ConsumptionDataContext'
import { UserContext } from '../../../../../contexts/UserContext'
import BaseChartScroller, { ChartScrollerProps } from '../../../components/BaseChartScroller'
import { ChartModes } from '../types/types'
import BaseSummary from './Summary'

interface ConsumptionChartScrollerProps
	extends Omit<ChartScrollerProps, 'canGoNext' | 'unit' | 'value'> {
	chartData: ConsumptionChartData | null
	chartModes: ChartModes
}

const ConsumptionChartScroller: React.FC<ConsumptionChartScrollerProps> = ({
	time,
	tooltipLoading,
	chartData,
	chartModes,
}) => {
	const { lastAvailableConsumptionData } = useContext(ConsumptionDataContext)
	const { t } = useTranslation('pages_electricity', { keyPrefix: 'electricityTimeScroller' })
	const { isAnonymousUser } = useContext(UserContext)

	const canGoNext = useMemo(() => {
		if (isAnonymousUser) return true
		if (!lastAvailableConsumptionData) return false

		const currentPeriodEnd = time.dayjs.endOf(time.span)
		const lastDataDate = dayjs(lastAvailableConsumptionData)

		return currentPeriodEnd.isBefore(lastDataDate)
	}, [lastAvailableConsumptionData, time.dayjs, time.span, isAnonymousUser])

	const totalConsumptionValue = useMemo(() => {
		return chartData?.consumption?.totalConsumption || 0
	}, [chartData])

	const transferTotalRaw = useMemo(() => {
		const transferTotal =
			chartData?.transferPrices.data.grouped.costWithoutElectricityPrice.totalValue ?? 0
		return transferTotal
	}, [chartData, chartModes])

	const transferDisplayValue = useMemo(() => {
		if (transferTotalRaw > 100) {
			return transferTotalRaw / 100
		} else {
			return transferTotalRaw
		}
	}, [transferTotalRaw])

	const transferUnit = useMemo(() => {
		if (transferTotalRaw > 100) {
			return '€'
		} else {
			return t('centsPrice')
		}
	}, [transferTotalRaw, t])

	const totalPriceRaw = useMemo(() => {
		let totalPrice = 0
		if (chartModes.exchangePrices && !chartModes.transferPrices) {
			totalPrice = chartData?.consumptionWithExchangePrice.totalValue ?? 0
		}
		if (!chartModes.exchangePrices && chartModes.transferPrices) {
			totalPrice = transferTotalRaw
		}
		if (chartModes.transferPrices && chartModes.exchangePrices) {
			totalPrice = chartData?.transferPrices.data.grouped.costWithElectricityPrice.totalValue ?? 0
		}

		// if no modes display consumption * price
		if (!chartModes.exchangePrices && !chartModes.transferPrices) {
			totalPrice = chartData?.consumptionWithExchangePrice.totalValue ?? 0
		}

		return totalPrice
	}, [chartData, chartModes, transferTotalRaw])

	const totalPriceDisplayValue = useMemo(() => {
		if (totalPriceRaw > 100) {
			return totalPriceRaw / 100
		} else {
			return totalPriceRaw
		}
	}, [totalPriceRaw])

	const totalPriceUnit = useMemo(() => {
		if (totalPriceRaw > 100) {
			return '€'
		} else {
			return t('centsPrice')
		}
	}, [totalPriceRaw, t])
	const averagePrice = useMemo(() => {
		if (!totalConsumptionValue || !totalPriceRaw) return 0
		return totalPriceRaw / totalConsumptionValue
	}, [totalPriceRaw, totalConsumptionValue, chartData])

	return (
		<BaseChartScroller
			time={time}
			tooltipLoading={tooltipLoading}
			unit={'kWh'}
			value={totalConsumptionValue}
			canGoNext={canGoNext}
		>
			<BaseSummary
				unit={totalPriceUnit}
				title={t('total')}
				value={totalPriceDisplayValue.toFixed(2).replace('.', ',')}
			/>

			{chartModes.transferPrices && chartModes.exchangePrices && (
				<>
					<Divider
						orientation='vertical'
						flexItem
						className='bg-background-200-light dark:bg-surface-dark'
					/>
					<div className='container'>
						<BaseSummary
							unit={transferUnit}
							title={t('transfer')}
							value={transferDisplayValue.toFixed(2).replace('.', ',')}
						/>
					</div>
				</>
			)}

			{(chartModes.exchangePrices || chartModes.transferPrices) && (
				<>
					<Divider
						orientation='vertical'
						flexItem
						className='bg-background-200-light dark:bg-surface-dark'
					/>

					<div className='container'>
						<BaseSummary
							unit={t('cents')}
							title={t('average')}
							value={averagePrice.toFixed(2).replace('.', ',')}
						/>
					</div>
				</>
			)}
		</BaseChartScroller>
	)
}

export default ConsumptionChartScroller
